/**
 * join-refusals.ts — why a way in could not join, said in the player's
 * language.
 *
 * The server refuses a join by a code (game-server/modules/shared/
 * join-refusals.go): the RPCs a way in calls before the match —
 * get_world_session, resolve_team_code, update_player_account_after_
 * authenticate, create_game_match — send it as their error, and the match's
 * join attempt as its reason, with a value after a colon when the code
 * carries one ("session_not_started:1790946000", when the session opens).
 * JOIN_REFUSALS is the one table every way in reads them by: the title, the
 * line under it, the tone — marigold where looking again will help, coral
 * where something stands in the way — whether Retry shows, and whether the
 * card adds whom to ask if it seems a mistake. A Go test holds its codes to
 * the server's (join_refusal_test.go).
 *
 * What the client meets on its own is read beside them: a game it cannot
 * reach — a fetch that fails, a socket that will not open, an answer that
 * never comes, a gateway's answer with no words of the server's while Nakama
 * restarts behind it — a match that is gone, over or lost, which Nakama's
 * answer cannot tell apart, and, for anything else, the
 * server's own words under a translated title. The looks a way in shows
 * around a join are here too: a link cut short (incompleteLink), a session
 * that opens later (notOpenYet), the wait for one whose match is not up yet
 * (waitingForStart).
 */

import i18n from '../i18n/config';
import { RpcRefusal, serverMessage } from '../services/nakama/http-rpc';
import { formatDayMonth, formatTimeOfDay } from '../utils/format';
import { refusalKey } from './order-block';

/** How a refusal sits on the way in's card. */
interface RefusalLook {
  /** The headline, a key. */
  title: string;
  /** The line under it, a key; none when the title says it all. */
  detail?: string;
  /** Marigold where looking again will help; coral where something stands in the way. */
  tone: 'warning' | 'danger';
  /** A second try can work: the card offers Retry. */
  retry: boolean;
  /** The card adds whom to ask if it seems a mistake: the administrator, or the portal. */
  contact: boolean;
}

/**
 * The server's refusals, by code: the session over; a moderator's removal,
 * which lasts a minute (no countdown, R3 — join_refusal_test.go holds the
 * minute to the server's); the session's match not up yet, or never made,
 * the scheduler's chance to make it past, or made and lost mid-session — the
 * server restarted — which no one but an administrator can start again, from
 * the portal: the player is told to let their instructor know, and Retry joins
 * the match if one is started; a link to no session; the test
 * session's own — a team with no session, a code no team has; a world the
 * portal does not hold, or one that cannot start its session's match; the
 * player's account not read or saved; and the portal not answering — said as
 * the portal, which is what failed, not the player's connection.
 */
const JOIN_REFUSALS = {
  session_ended: { title: 'entry.refusal.ended', tone: 'danger', retry: false, contact: true },
  removed: { title: 'ban.notification.title', detail: 'entry.refusal.removedDetail', tone: 'danger', retry: true, contact: true },
  session_not_started: { title: 'entry.refusal.notStarted', detail: 'entry.refusal.waitThenRetry', tone: 'warning', retry: true, contact: false },
  session_never_started: { title: 'entry.refusal.neverStarted', detail: 'entry.refusal.askInstructor', tone: 'danger', retry: false, contact: false },
  session_match_lost: { title: 'entry.refusal.matchLost', detail: 'entry.refusal.matchLostDetail', tone: 'danger', retry: true, contact: false },
  session_not_found: { title: 'entry.refusal.notFound', detail: 'entry.refusal.notFoundDetail', tone: 'danger', retry: false, contact: false },
  no_active_session: { title: 'entry.refusal.noActiveSession', detail: 'entry.refusal.askInstructor', tone: 'danger', retry: false, contact: false },
  team_code_not_found: { title: 'entry.refusal.codeNotFound', tone: 'danger', retry: false, contact: false },
  world_not_found: { title: 'entry.refusal.worldNotFound', detail: 'entry.refusal.worldNotFoundDetail', tone: 'danger', retry: false, contact: false },
  world_cannot_start: { title: 'entry.refusal.worldCannotStart', detail: 'entry.refusal.worldCannotStartDetail', tone: 'danger', retry: true, contact: false },
  account_unavailable: { title: 'entry.refusal.accountUnavailable', detail: 'entry.refusal.waitThenRetry', tone: 'danger', retry: true, contact: true },
  portal_unreachable: { title: 'entry.refusal.portalUnreachable', detail: 'entry.refusal.waitThenRetry', tone: 'danger', retry: true, contact: true },
} as const;

/** A code the server refuses a join with. */
type JoinRefusalCode = keyof typeof JOIN_REFUSALS;

/**
 * The looks the client says on its own, beside the server's, since no server
 * sends them.
 */
const CLIENT_LOOKS = {
  /**
   * A game the client could not reach — a fetch that failed, a socket that
   * would not open, an answer that never came, a gateway's wordless 5xx.
   */
  unreachable: { title: 'entry.refusal.unreachable', detail: 'entry.refusal.unreachableDetail', tone: 'danger', retry: true, contact: true },
  /**
   * A match a join names by its id that is not there — the staff's ways in,
   * which join the match their link names: Nakama answers the same for a match
   * over with its session and one lost as the server restarted, so the card
   * says only that it is not running, and the staff's caption the way back,
   * the portal. A second try at the same match cannot work.
   */
  matchGone: { title: 'entry.refusal.matchGone', tone: 'danger', retry: false, contact: true },
} satisfies Record<string, RefusalLook>;

/** A look the client says on its own. */
type ClientLookCode = keyof typeof CLIENT_LOOKS;

/** Every look, the server's by code and the client's own: the type checks both tables. */
const LOOKS: Record<JoinRefusalCode | ClientLookCode, RefusalLook> = { ...JOIN_REFUSALS, ...CLIENT_LOOKS };

/** A refused join, as a way in keeps it. */
export type JoinRefusal =
  /** The server's, by its code, with the value it carries. */
  | { code: JoinRefusalCode; value?: string }
  /** The client's own: the game not reached, or the match a join named not there. */
  | { code: ClientLookCode }
  /** Anything else, in the server's own words. */
  | { code: 'unknown'; words: string };

/** A refusal's words on the card, in the player's language, with its look. */
export interface RefusalWords {
  title: string;
  /** The line under the title: what to do, or the server's own words. */
  detail?: string;
  tone: RefusalLook['tone'];
  retry: boolean;
  contact: boolean;
}

/** A refusal as the server sends it: its code, and a value after a colon. Anything else is its own words. */
export function refusalFromWords(words: string): JoinRefusal {
  const [, code, value] = /^([a-z_]+)(?::(.*))?$/.exec(words.trim()) ?? [];
  if (refusalKey(JOIN_REFUSALS, code) === undefined) return { code: 'unknown', words };
  return value === undefined ? { code: code as JoinRefusalCode } : { code: code as JoinRefusalCode, value };
}

/**
 * Whether a failed join never reached the game: a fetch the network refused
 * (a TypeError, in each browser's words), a socket's error event, or an
 * answer that never came (nakama-js's "…timed out…").
 */
function unreachable(error: unknown): boolean {
  if (error instanceof TypeError) return /fetch|network|load failed/i.test(error.message);
  if (typeof Event !== 'undefined' && error instanceof Event) return true;
  return typeof error === 'string' && /timed out/i.test(error);
}

/** Nakama's realtime code for a match that is not there: one over with its session, or lost as the server restarted. */
const MATCH_NOT_FOUND = 4;

/**
 * Whether a join failed on a match that is not there (MATCH_NOT_FOUND, as the
 * socket rejects it): over, or lost, and no second try at the same match can
 * work — a way in says so (matchGone), and a reconnect stops trying.
 */
export function isMatchGone(error: unknown): boolean {
  return !!error && typeof error === 'object' && 'code' in error && error.code === MATCH_NOT_FOUND && !(error instanceof Error);
}

/** What a refused call answered: its HTTP status where it had one, and the server's own words, or null for none. */
interface Answer {
  status?: number;
  words: string | null;
}

/**
 * What whatever a failed join threw answered: a refused RPC (RpcRefusal), a
 * refused HTTP call (nakama-js throws the Response), a socket's error
 * (`{ code, message }`), an Error, or a string.
 */
async function answerOf(error: unknown): Promise<Answer> {
  if (error instanceof RpcRefusal) return { status: error.status, words: error.words };
  if (typeof Response !== 'undefined' && error instanceof Response) {
    const body = await error.text().catch(() => '');
    return { status: error.status, words: serverMessage(body) };
  }
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') return { words: error.message };
  return { words: typeof error === 'string' ? error : null };
}

/**
 * The refusal a failed join met, from whatever it threw. A match that is not
 * there is a match gone — not the session's end, which a match lost as the
 * server restarted mid-session is not, and Nakama's answer cannot tell the two
 * apart — and a gateway's answer with no words of the server's (a 5xx while
 * Nakama restarts behind its proxy) is the game not reached.
 */
export async function readJoinRefusal(error: unknown): Promise<JoinRefusal> {
  if (unreachable(error)) return { code: 'unreachable' };
  if (isMatchGone(error)) return { code: 'matchGone' };
  const { status, words } = await answerOf(error);
  if (words !== null) return refusalFromWords(words);
  if (status !== undefined && status >= 500) return { code: 'unreachable' };
  return { code: 'unknown', words: status !== undefined ? `HTTP ${status}` : '' };
}

/** When the session opens, in Unix seconds, from a refusal that says it; null when it says none. */
export function refusalOpensAt(refusal: JoinRefusal): number | null {
  if (refusal.code !== 'session_not_started' || refusal.value === undefined) return null;
  const seconds = Number(refusal.value);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
}

/**
 * The words a refusal is said in: the table's, by its code. A session not
 * started that says when it opens says so, as a link opened before its
 * window does (notOpenYet); a game not reached and a match gone say so, in
 * the client's own looks; anything else is the server's words under a title.
 */
export function refusalWords(refusal: JoinRefusal): RefusalWords {
  const t = i18n.t.bind(i18n);
  if (refusal.code === 'unknown') {
    return { title: t('entry.refusal.other'), detail: refusal.words || undefined, tone: 'danger', retry: true, contact: true };
  }
  const opens = refusalOpensAt(refusal);
  if (opens !== null) return { ...notOpenYet(opens), retry: true };
  const look = LOOKS[refusal.code];
  return { title: t(look.title), detail: look.detail && t(look.detail), tone: look.tone, retry: look.retry, contact: look.contact };
}

/**
 * A session that opens later: when, in the player's language — the moment the
 * server opens it, which a session link reads with its session
 * (get_world_session) and a team whose session is still to come hears in its
 * refusal (resolve_team_code), so the two say the same time.
 */
export function notOpenYet(opensAt: number): RefusalWords {
  const at = new Date(opensAt * 1000);
  return {
    title: i18n.t('entry.refusal.early'),
    detail: i18n.t('entry.refusal.earlyDetail', { date: formatDayMonth(at), time: formatTimeOfDay(at) }),
    tone: 'warning',
    retry: false,
    contact: true,
  };
}

/**
 * How a way in waits for a session that has not started: it asks again this
 * often, for about a minute, before it hands Retry to the player (R1).
 */
export const NOT_STARTED_WAIT = { everyMs: 5_000, forMs: 60_000 } as const;

/** Whether a refusal is one a way in waits through: the session's match not up yet, no opening named. */
export function waitsForStart(refusal: JoinRefusal | null): boolean {
  return refusal?.code === 'session_not_started' && refusalOpensAt(refusal) === null;
}

/** How a join stands, as a wait for its session reads it. */
interface JoinWaitState {
  refusal: JoinRefusal | null;
  /** When the wait runs out, in epoch milliseconds; null when there is none. */
  waitUntil: number | null;
  /** A join is on its way: sent again, or the player's own. */
  connecting: boolean;
  /** Now, in epoch milliseconds. */
  nowMs: number;
}

/**
 * Whether a way in is waiting for its session to start (R1): the wait has not
 * run out, and the join is either refused for a session not up yet or on its
 * way again.
 */
export function isWaitingForStart({ refusal, waitUntil, connecting, nowMs }: JoinWaitState): boolean {
  return waitUntil !== null && nowMs < waitUntil && (connecting || waitsForStart(refusal));
}

/** The wait for a session not up yet, as the card says it: the table's look, what the page does, and no Retry while it waits. */
export function waitingForStart(): RefusalWords {
  return { ...refusalWords({ code: 'session_not_started' }), detail: i18n.t('entry.waitingDetail'), retry: false };
}

/**
 * A link cut short: only a new one mends it, so no Retry. The line under it
 * says where to get one, or — given as `detail` where the card has it — what
 * the link lacks.
 */
export function incompleteLink(detail?: string): RefusalWords {
  return { title: i18n.t('entry.refusal.invalidLink'), detail, tone: 'danger', retry: false, contact: false };
}
